import type { Employee, PayrollSlipShape } from '../domain';
import { toast } from '../components/toast';

const W35_DATES = ['2026-08-25','2026-08-26','2026-08-27','2026-08-28','2026-08-29'];

export const PERIOD_LABEL = 'Aug 25 → Aug 31 · W35';

export function computePayslip(emp: Employee, dates: string[]): PayrollSlipShape {
  const base = emp.baseRatePerHour ?? emp.rate ?? 100;
  const hours = emp.hours ?? dates.length * 8;
  const ot = emp.otHours ?? 0;
  const gross = Math.round(hours * base);
  const otPay = Math.round(ot * base * 1.5);
  const grossTotal = gross + otPay;
  const pf = Math.round(grossTotal * 0.06);
  const profTax = grossTotal > 10000 ? 200 : 0;
  const tdsApplied = grossTotal > 25000;
  const tds = tdsApplied ? Math.round((grossTotal - 25000) * 0.05) : 0;
  const totalDed = pf + profTax + tds;
  return { hours, otHours: ot, gross, otPay, grossTotal, pf, profTax, tds, totalDed, net: Math.max(grossTotal - totalDed, 0), tdsApplied };
}


function fmtINR(n: number): string {
  return '₹' + Math.round(n).toLocaleString('en-IN');
}

function makeSlipHTML(emp: Employee, slip: PayrollSlipShape, periodLabel: string, tenantName: string): string {
  return `
    <article class="payslip-page" data-employee="${emp.uid}">
      <header class="ps-head">
        <div class="ps-brand">
          <div class="ps-logo"></div>
          <div>
            <div class="ps-bn">${escapeHtml(tenantName)}</div>
            <div class="ps-tag">Payslip · ${escapeHtml(periodLabel)}${slip.tdsApplied ? '' : ' · TDS threshold not met'}</div>
          </div>
        </div>
        <div class="ps-id">
          <div class="t-mono"><span class="ps-k">UID</span>${escapeHtml(emp.uid)}</div>
          <div class="t-mono"><span class="ps-k">Issued</span>29 Aug 2026</div>
        </div>
      </header>
      <section class="ps-emp">
        <h3>Employee</h3>
        <div class="ps-emp-grid">
          <div><span>Name</span><strong>${escapeHtml(emp.name)}</strong></div>
          <div><span>Role</span><strong>${escapeHtml(emp.role)}</strong></div>
          <div><span>Dept</span><strong>${escapeHtml(emp.department)}</strong></div>
          <div><span>Joined</span><strong>${escapeHtml(emp.joinedAt)}</strong></div>
          <div><span>Email</span><strong>${escapeHtml(emp.email)}</strong></div>
          <div><span>Phone</span><strong>${escapeHtml(emp.phone)}</strong></div>
        </div>
      </section>
      <section class="ps-earn">
        <h3>Earnings</h3>
        <table class="ps-table">
          <tbody>
            <tr><td>${slip.hours}h × ${fmtINR(emp.baseRatePerHour)}/hr (base)</td><td class="ps-amt">${fmtINR(slip.gross)}</td></tr>
            ${slip.otHours > 0 ? `<tr><td>${slip.otHours}h overtime @ 1.5×</td><td class="ps-amt">${fmtINR(slip.otPay)}</td></tr>` : ''}
            <tr class="ps-total"><td><strong>Gross earnings</strong></td><td class="ps-amt"><strong>${fmtINR(slip.grossTotal)}</strong></td></tr>
          </tbody>
        </table>
      </section>
      <section class="ps-ded">
        <h3>Deductions</h3>
        <table class="ps-table">
          <tbody>
            <tr><td>Provident Fund (6%)</td><td class="ps-amt">−${fmtINR(slip.pf)}</td></tr>
            <tr><td>Professional Tax</td><td class="ps-amt">−${fmtINR(slip.profTax)}</td></tr>
            ${slip.tdsApplied
              ? `<tr><td>TDS (5% over ₹${(25000).toLocaleString('en-IN')})</td><td class="ps-amt">−${fmtINR(slip.tds)}</td></tr>`
              : `<tr><td colspan="2" class="t-mono t-muted" style="font-size:9px; font-style:italic; padding-top:2px;">TDS not applied — gross below ₹${(25000).toLocaleString('en-IN')} threshold</td></tr>`}
            <tr class="ps-total"><td><strong>Total deductions</strong></td><td class="ps-amt"><strong>−${fmtINR(slip.totalDed)}</strong></td></tr>
          </tbody>
        </table>
      </section>
      <section class="ps-net-row">
        <div>
          <span class="t-eyebrow">Net pay</span>
          <div class="ps-net">${fmtINR(slip.net)}</div>
          <div class="t-meta">${slip.hours}h worked · period ${escapeHtml(periodLabel)}</div>
        </div>
        <div class="ps-bank">
          <span class="t-eyebrow">Bank payout</span>
          <div class="t-mono">${escapeHtml(emp.bank.upi)}</div>
          <div class="t-mono t-meta">AC •••• ${escapeHtml(emp.bank.last4)} · ${escapeHtml(emp.bank.ifsc)}</div>
        </div>
      </section>
      <footer class="ps-foot">
        <span>System-generated payslip. No signature required.</span>
        <span>Generated by Clientale · ${escapeHtml(tenantName)}</span>
      </footer>
    </article>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

function ensurePrintHost(): HTMLElement {
  let host = document.getElementById('print-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'print-host';
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
  }
  return host;
}

// --- Print-dialog guard -----------------------------------------------------
// Chrome queues a NEW dialog per window.print() call. If a stale key handler or
// a stacked click listener fires twice, the dialogs pile up forever (the
// "print loop" bug). We gate with a flag and release on afterprint (dialog
// closes via Print OR Cancel) plus a safety timeout.
let printBusy = false;
function openPrintDialog() {
  if (printBusy) {
    toast('Print busy — cancel the current dialog to continue', 'warn');
    // Backup path: if for any reason the dialog was closed but afterprint didn't
    // fire within the lock window, hard-release after 2.5s so the NEXT click works.
    setTimeout(() => { printBusy = false; }, 2500);
    return;
  }
  printBusy = true;
  const release = () => {
    printBusy = false;
    window.removeEventListener('afterprint', release);
    window.removeEventListener('focus', onFocus);
  };
  const onFocus = () => release(); // dialog closed → focus returns to main window
  window.addEventListener('afterprint', release, { once: true });
  window.addEventListener('focus', onFocus, { once: true });
  setTimeout(() => window.print(), 60);
  // HARD release after 2s — guarantees every legitimate next click works.
  setTimeout(release, 2000);
}

export function printPayslip(emp: Employee, slipOrDates: PayrollSlipShape | string[], periodLabel: string = PERIOD_LABEL, tenantName: string = 'tech tatwa') {
  const slip = Array.isArray(slipOrDates) ? computePayslip(emp, slipOrDates) : slipOrDates;
  const host = ensurePrintHost();
  host.innerHTML = makeSlipHTML(emp, slip, periodLabel, tenantName);
  toast(`Opening print dialog · "Save as PDF" will download ${payslipFileName(emp, periodLabel)}${slip.tdsApplied ? '' : ' · no TDS'}`, 'success');
  openPrintDialog();
}

export function bulkExportPayslipsPDF(emps: Employee[], dates: string[], periodLabel: string = PERIOD_LABEL, tenantName: string = 'tech tatwa') {
  const host = ensurePrintHost();
  host.innerHTML = emps.map((e) => {
    const slip = computePayslip(e, dates);
    return makeSlipHTML(e, slip, periodLabel, tenantName);
  }).join('');
  toast(`Opening print dialog · ${emps.length} payslips · pick "Save as PDF"`, 'success');
  openPrintDialog();
}

export function payslipFileName(emp: Employee, periodLabel: string): string {
  const safe = periodLabel.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase();
  return `payslip_${emp.uid}_${safe}.pdf`;
}

export function previewPayslip(emp: Employee, dates: string[]) { return computePayslip(emp, dates); }

export const W35_DATES_EXPORT = W35_DATES;

